import { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import { head } from "lodash";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { findQueueById, deleteQueueMedia } from "../../services/queue.api";

// D8.1.4c-1 — Modelo de datos y ciclo de vida del form del modal de colas:
// carga inicial (loadQueueData), sincronización de flowBuilder, recarga defensiva,
// adjunto y valores iniciales de Formik. IN: open, queueId, flowBuilders (catálogo).
export default function useQueueForm({ open, queueId, flowBuilders }) {
  const initialState = useMemo(() => ({
    name: "",
    color: "",
    greetingMessage: "",
    outOfHoursMessage: "",
    orderQueue: "",
    integrationId: "",
    promptId: "",
    flowBuilderId: "",
    contingencyQueueId: "",
    contingencyMessage: "",
    aiFallbackEnabled: false,
    aiFallbackProvider: "",
    aiFallbackApiKey: ""
  }), []);

  const [queueData, setQueueData] = useState(initialState);
  const [attachment, setAttachment] = useState(null);
  const attachmentFile = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [schedules, setSchedules] = useState([
    { weekday: "Lunes", weekdayEn: "monday", startTime: "08:00", endTime: "18:00", },
    { weekday: "Martes", weekdayEn: "tuesday", startTime: "08:00", endTime: "18:00", },
    { weekday: "Miércoles", weekdayEn: "wednesday", startTime: "08:00", endTime: "18:00", },
    { weekday: "Jueves", weekdayEn: "thursday", startTime: "08:00", endTime: "18:00", },
    { weekday: "Viernes", weekdayEn: "friday", startTime: "08:00", endTime: "18:00", },
    { weekday: "Sábado", weekdayEn: "saturday", startTime: "08:00", endTime: "12:00", },
    { weekday: "Domingo", weekdayEn: "sunday", startTime: "00:00", endTime: "00:00", },
  ]);
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [selectedFlowBuilder, setSelectedFlowBuilder] = useState('');

  // Carga inicial de datos de la cola
  useEffect(() => {
    let isMounted = true;

    const loadQueueData = async () => {
      if (!open) {
        return;
      }

      if (!queueId) {
        // Solo para crear nuevo departamento
        setQueueData(initialState);
        setSelectedPrompt('');
        setSelectedFlowBuilder('');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const { data } = await findQueueById(queueId);

        if (!isMounted) return;

        if (!data) {
          throw new Error('No data received from API');
        }

        const formattedData = {
          name: data.name || "",
          color: data.color || "",
          greetingMessage: data.greetingMessage || "",
          outOfHoursMessage: data.outOfHoursMessage || "",
          orderQueue: data.orderQueue !== null && data.orderQueue !== undefined ? data.orderQueue : "",
          integrationId: data.integrationId !== null && data.integrationId !== undefined ? data.integrationId : "",
          promptId: data.promptId || "",
          flowBuilderId: data.flowBuilderId || "",
          contingencyQueueId: data.contingencyQueueId != null ? String(data.contingencyQueueId) : "",
          contingencyMessage: data.contingencyMessage || "",
          aiFallbackEnabled: data.aiFallbackEnabled || false,
          aiFallbackProvider: data.aiFallbackProvider || "",
          aiFallbackApiKey: data.aiFallbackApiKey || "",
          // La clave ya no vuelve al panel (doc 0431, A-10): solo si existe y su final.
          aiFallbackApiKeyPreview: data.aiFallbackApiKeyPreview || null
        };

        if (isMounted) {
          setQueueData(formattedData);
          setSelectedPrompt(data.promptId || '');
          // No establecer flowBuilder hasta que se carguen los flowBuilders (evita warning MUI)
          setSelectedFlowBuilder('');
          if (data.schedules) {
            setSchedules(data.schedules);
          }
          setDataLoaded(true);
          setIsLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          toastError(err);
          setQueueData(initialState);
          setSelectedPrompt('');
          setSelectedFlowBuilder('');
          setIsLoading(false);
        }
      }
    };

    loadQueueData();

    return () => {
      isMounted = false;
    };
  }, [open, queueId, initialState]);

  // Establecer flowBuilderId tras cargar flowBuilders y datos (evita warning MUI de valor fuera de rango)
  useEffect(() => {
    if (!open || !dataLoaded || flowBuilders.length === 0) return;

    const flowBuilderIdFromData = queueData.flowBuilderId;

    if (flowBuilderIdFromData) {
      const flowBuilderIdStr = String(flowBuilderIdFromData);
      const flowBuilderExists = flowBuilders.some(fb => String(fb.id) === flowBuilderIdStr);

      if (flowBuilderExists) {
        if (selectedFlowBuilder !== flowBuilderIdStr) {
          setSelectedFlowBuilder(flowBuilderIdStr);
        }
      } else {
        console.warn(`[QueueModal] FlowBuilder ID ${flowBuilderIdStr} no encontrado en la lista disponible`);
        if (selectedFlowBuilder) {
          setSelectedFlowBuilder('');
        }
      }
    } else {
      if (selectedFlowBuilder) {
        setSelectedFlowBuilder('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dataLoaded, flowBuilders, queueData]);

  // Evitar que se limpien los datos una vez cargados
  useEffect(() => {
    if (dataLoaded && queueId && queueData.name === "") {
      const reloadData = async () => {
        try {
          const { data } = await findQueueById(queueId);
          if (data) {
            const formattedData = {
              name: data.name || "",
              color: data.color || "",
              greetingMessage: data.greetingMessage || "",
              outOfHoursMessage: data.outOfHoursMessage || "",
              orderQueue: data.orderQueue !== null && data.orderQueue !== undefined ? data.orderQueue : "",
              integrationId: data.integrationId !== null && data.integrationId !== undefined ? data.integrationId : "",
              promptId: data.promptId || ""
            };
            setQueueData(formattedData);
            setSelectedPrompt(data.promptId || '');
          }
        } catch (err) {
          console.error("Error al recargar datos:", err);
        }
      };
      reloadData();
    }
  }, [dataLoaded, queueId, queueData.name]);

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
    }
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
    }

    if (queueData.mediaPath) {
      await deleteQueueMedia(queueData.id);
      setQueueData((prev) => ({ ...prev, mediaPath: null, mediaName: null }));
      toast.success(i18n.t("queueModal.toasts.deleted"));
    }
  };

  // Valores iniciales estables para Formik (color excluido de deps para no reinicializar)
  const formikInitialValues = useMemo(() => {
    const gm = queueData.greetingMessage || "";
    const values = {
      name: queueData.name || "",
      color: queueData.color || "",
      greetingMessage: gm,
      greetingEnabled: !!(gm && String(gm).trim()),
      outOfHoursMessage: queueData.outOfHoursMessage || "",
      orderQueue: queueData.orderQueue !== null && queueData.orderQueue !== undefined ? queueData.orderQueue : "",
      integrationId: queueData.integrationId !== null && queueData.integrationId !== undefined ? queueData.integrationId : "",
      promptId: queueData.promptId || "",
      contingencyQueueId: queueData.contingencyQueueId || "",
      contingencyMessage: queueData.contingencyMessage || "",
      aiFallbackEnabled: queueData.aiFallbackEnabled || false,
      aiFallbackProvider: queueData.aiFallbackProvider || "",
      aiFallbackApiKey: queueData.aiFallbackApiKey || ""
    };
    return values;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueData.name, queueData.greetingMessage, queueData.outOfHoursMessage, queueData.orderQueue, queueData.integrationId, queueData.promptId, queueData.contingencyQueueId, queueData.contingencyMessage, queueData.aiFallbackEnabled, queueData.aiFallbackProvider, queueData.aiFallbackApiKey, dataLoaded]);

  return {
    initialState,
    queueData,
    setQueueData,
    attachment,
    setAttachment,
    attachmentFile,
    isLoading,
    setIsLoading,
    dataLoaded,
    setDataLoaded,
    schedules,
    setSchedules,
    selectedPrompt,
    setSelectedPrompt,
    selectedFlowBuilder,
    setSelectedFlowBuilder,
    formikInitialValues,
    handleAttachmentFile,
    deleteMedia,
  };
}
